import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {
  selectGameResults,
  selectGameType,
  selectPhase,
  selectPlayers,
} from "../../redux/reducers/sharedStateReducer/selectors";
import {selectAuthState, selectLobbyId} from "../../redux/reducers/privateStateReducer/selectors";
import {GAME_TYPES} from "../../constants/game";
import {getOwnPlayerId} from "../../utils/players/getOwnPlayerId";
import {PHASES} from "../../constants/phases";
import BotControlModal from "../Modals/BotControlModal/BotControlModal";
import {checkIfBotControlTakeoverAllowed} from "../../utils/bots/online/controlledBots";
import {PlayerInfo} from "../PlayerInfo/PlayerInfo";
import FactionCardOverlay from "../FactionCardOverlay/FactionCardOverlay";
import './PlayersList.css'
import PolicyCardOverlay from "../PolicyCardOverlay/PolicyCardOverlay";
import {usePlayersListSideMenu} from "../../contexts/PlayersListSideMenuContext";
import {
  selectConstructionPlanStatsView
} from "../../utils/gameState/construction/planning/buildConstructionPlanningViewState";
import {useGameUiPortalTarget} from "../gameUi/GameUiPortal";

// Окно, в которое второе нажатие по боту считается двойным. Одиночное
// нажатие (карточка фракции) у таких строк откладывается на это время: сразу
// открытая карточка перекрыла бы строку, и второе нажатие попало бы в нее
// (тот же случай, что в CompletePhaseButton)
const BOT_DOUBLE_TAP_MS = 300;

const PlayersList = () => {
  const players = useSelector(selectPlayers);
  const gameType = useSelector(selectGameType);
  const gameResults = useSelector(selectGameResults);
  const phase = useSelector(selectPhase);
  const auth = useSelector(selectAuthState);
  const lobbyId = useSelector(selectLobbyId);
  // Своя строка планирующего игрока — с монетами за вычетом запланированных
  // строений, как в шапке и на ярлыке панели. Чужие строки — настоящие: план
  // живет только у своего клиента
  const planView = useSelector(selectConstructionPlanStatsView);

  // Перехват управления ботом: только админ, только в сетевом матче, где он
  // сам не участник, и только пока матч идет (см. controlledBots)
  const isAdminUser = Boolean(auth?.isAdmin || auth?.isSuperAdmin);
  const isMatchParticipant = players.some((player) => player.id === auth?.id);
  const canControlBots = gameType === GAME_TYPES.ONLINE && isAdminUser && !isMatchParticipant
    && !(gameResults && gameResults.length > 0);

  const [botControlTarget, setBotControlTarget] = useState(null);
  const tapTimerRef = useRef(null);
  const tapPlayerIdRef = useRef(null);
  useEffect(() => () => clearTimeout(tapTimerRef.current), []);
  const {
    selectedPlayer,
    setSelectedPlayer,
    selectedPolicy,
    setSelectedPolicy,
  } = usePlayersListSideMenu();
  // Карточки — поверх всего экрана матча, а не внутри выпадающего списка:
  // в списке высотой по содержимому полноразмерная карточка была бы зажата.
  // Список смонтирован всегда, поэтому карточка с кнопки хода (список при
  // этом закрыт) рендерится отсюда же
  const portalTarget = useGameUiPortalTarget();

  // Явный бот — не цель перехвата: нажатие по нему как по живому игроку, и
  // подписи «Бот» у него нет — ботом его уже показывает значок лиги
  const isTakeoverTarget = (player) => canControlBots && checkIfBotControlTakeoverAllowed(player);

  const handlePlayerClick = (player) => {
    if (!isTakeoverTarget(player)) {
      setSelectedPlayer(player);
      return;
    }

    // Второе нажатие по тому же боту в окне — двойное: вместо карточки
    // фракции открываем подтверждение перехвата
    if (tapTimerRef.current && tapPlayerIdRef.current === player.id) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
      tapPlayerIdRef.current = null;
      setBotControlTarget(player);
      return;
    }

    clearTimeout(tapTimerRef.current);
    tapPlayerIdRef.current = player.id;
    tapTimerRef.current = setTimeout(() => {
      tapTimerRef.current = null;
      tapPlayerIdRef.current = null;
      setSelectedPlayer(player);
    }, BOT_DOUBLE_TAP_MS);
  };

  const getControlLabel = (player) => {
    if (!isTakeoverTarget(player)) return null;
    if (player.botControlledByUserId === auth?.id) return 'Бот·ВЫ';
    if (player.botControlledByUserId) return 'Бот·АДМИН';
    return 'Бот';
  };

  // Своя карточка в списке — с лентой «вы» (см. getOwnPlayerId)
  const ownPlayerId = getOwnPlayerId(gameType, auth?.id, players);

  // Бот для модалки берется из свежего состояния: пометка перехвата могла
  // смениться, пока модалка открыта
  const botControlTargetFresh = botControlTarget
    ? players.find((player) => player.id === botControlTarget.id) || null
    : null;

  return (
    <>
      <div className={'player-list'}>
        {players.map(player => {
          return <PlayerInfo
            key={player.id}
            player={planView && planView.player.id === player.id ? planView.player : player}
            isOwn={player.id === ownPlayerId}
            onClick={() => handlePlayerClick(player)}
            controlLabel={getControlLabel(player)}
            onPolicyClick={(policy) => {
              setSelectedPlayer(player)
              setSelectedPolicy(policy)
            }}
          />;
        })}
      </div>

      {createPortal(
        <FactionCardOverlay
          factionId={!selectedPolicy && selectedPlayer?.factionId}
          skinId={!selectedPolicy && selectedPlayer?.selectedSkin}
          onClose={() => setSelectedPlayer(null)}
        />,
        portalTarget
      )}

      {canControlBots && botControlTargetFresh && (
        <BotControlModal
          bot={botControlTargetFresh}
          players={players}
          userId={auth?.id}
          lobbyId={lobbyId}
          isSetupPhase={phase === PHASES.SETUP}
          onClose={() => setBotControlTarget(null)}
        />
      )}

      {createPortal(
        <PolicyCardOverlay
          selectedPolicy={selectedPolicy}
          onClose={() => {
            setSelectedPlayer(null)
            setSelectedPolicy(null)
          }}
        />,
        portalTarget
      )}
    </>
  );
};

export default PlayersList;
